import Image from "next/image";
import type { CSSProperties, ReactNode } from "react";
import { useId } from "react";

/**
 * Dashboard building blocks for the service-page demos (SEO, webbyrå):
 * white cards, a big KPI, a column chart and an area chart, plus icons.
 * Markup only; styles live under `.cc-demo` in `_seo-page.scss`.
 */

export const svgProps = {
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export const Icons = {
  search: (
    <svg {...svgProps}>
      <circle cx="7" cy="7" r="4.5" />
      <path d="m10.5 10.5 3 3" />
    </svg>
  ),
  lock: (
    <svg {...svgProps}>
      <rect x="3.5" y="7" width="9" height="6.5" rx="1.5" />
      <path d="M5.5 7V5.5a2.5 2.5 0 0 1 5 0V7" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 16 16" fill="currentColor" stroke="none">
      <path d="m8 1.8 1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />
    </svg>
  ),
  up: (
    <svg {...svgProps}>
      <path d="M8 12.5v-9M4.5 7 8 3.5 11.5 7" />
    </svg>
  ),
  page: (
    <svg {...svgProps}>
      <path d="M4 2.5h5.5L12 5v8.5H4z" />
      <path d="M9.5 2.5V5H12" />
    </svg>
  ),
  inbox: (
    <svg {...svgProps}>
      <path d="M2.5 9.5 4 3.5h8l1.5 6v3h-11z" />
      <path d="M2.5 9.5h3l1 1.5h3l1-1.5h3" />
    </svg>
  ),
  chart: (
    <svg {...svgProps}>
      <path d="M2.5 13.5h11M4.5 11V8M8 11V5M11.5 11V3" />
    </svg>
  ),
  funnel: (
    <svg {...svgProps}>
      <path d="M2.5 3h11L9.5 8v5l-3-1.5V8z" />
    </svg>
  ),
};

/** White panel with a small title row, like a dashboard card. */
export function Panel({
  title,
  icon,
  meta,
  className = "",
  children,
}: {
  title?: string;
  icon?: ReactNode;
  meta?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`cc-demo__panel ${className}`.trim()}>
      {title ? (
        <div className="cc-demo__panel-head">
          {icon ? (
            <span className="cc-demo__panel-icon" aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <p className="cc-demo__panel-title">{title}</p>
          {meta ? <span className="cc-demo__panel-meta">{meta}</span> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** Big number + period + green change chip. */
export function Kpi({
  value,
  period,
  change,
}: {
  value: string;
  period: string;
  change: string;
}) {
  return (
    <div className="cc-demo-kpi">
      <p className="cc-demo-kpi__value">{value}</p>
      <p className="cc-demo-kpi__meta">
        <span>{period}</span>
        <span className="cc-demo-chip cc-demo-chip--up">
          <span className="cc-demo-chip__icon" aria-hidden="true">
            {Icons.up}
          </span>
          {change}
        </span>
      </p>
    </div>
  );
}

/** Teal column chart; the last bar is the current period. */
export function Bars({ series, labels }: { series: number[]; labels: string[] }) {
  const max = Math.max(...series);
  return (
    <div className="cc-demo-bars" aria-hidden="true">
      {series.map((value, index) => (
        <span key={index} className="cc-demo-bars__col">
          <span
            className={`cc-demo-bars__bar${index === series.length - 1 ? " is-current" : ""}`}
            style={{ "--cc-h": value / max } as CSSProperties}
          />
          <span className="cc-demo-bars__label">{labels[index]}</span>
        </span>
      ))}
    </div>
  );
}

/** Smooth area chart drawn as one SVG path. */
export function Area({ series }: { series: number[] }) {
  // Unique per render: the site chrome keeps a hidden copy of the page.
  const gradientId = `cc-demo-area-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const width = 300;
  const height = 96;
  const max = Math.max(...series) * 1.08;
  const points = series.map((value, index) => [
    (index / (series.length - 1)) * width,
    height - (value / max) * height,
  ]);
  const line = points
    .map(([x, y], index) => `${index === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(" ");
  const [lastX, lastY] = points[points.length - 1];
  return (
    <svg
      className="cc-demo-area"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3aafa9" stopOpacity="0.28" />
          <stop offset="1" stopColor="#3aafa9" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((f) => (
        <path key={f} d={`M0 ${height * f} H${width}`} className="cc-demo-area__grid" />
      ))}
      <path d={`${line} L${width} ${height} L0 ${height} Z`} fill={`url(#${gradientId})`} />
      <path d={line} className="cc-demo-area__line" />
      <circle cx={lastX} cy={lastY} r="3.5" className="cc-demo-area__dot" />
    </svg>
  );
}


export type InboxRow = {
  initials: string;
  /** Optional portrait; the initials show when there is none. */
  photo?: string;
  name: string;
  subject: string;
  time: string;
  status: string;
  tone: "new" | "pending" | "won";
};

/** Enquiries inbox: first row unread, each row with source and status chips. */
export function InboxPanel({
  title,
  meta,
  source,
  rows,
}: {
  title: string;
  meta: string;
  source: string;
  rows: InboxRow[];
}) {
  return (
    <Panel
      className="cc-demo__panel--main cc-demo-inbox"
      title={title}
      icon={Icons.inbox}
      meta={meta}
    >
      <ul className="cc-demo-inbox__rows">
        {rows.map((row, index) => (
          <li
            key={row.name}
            className={`cc-demo-inbox__row${index === 0 ? " is-unread" : ""}`}
          >
            <span className={`cc-demo-inbox__avatar is-${index % 4}`} aria-hidden="true">
              {row.photo ? (
                <Image src={row.photo} alt="" width={34} height={34} />
              ) : (
                row.initials
              )}
            </span>
            <span className="cc-demo-inbox__body">
              <span className="cc-demo-inbox__line">
                <span className="cc-demo-inbox__name">{row.name}</span>
                <span className="cc-demo-inbox__source">{source}</span>
                <span className="cc-demo-inbox__time">{row.time}</span>
              </span>
              <span className="cc-demo-inbox__subject">{row.subject}</span>
            </span>
            <span className={`cc-demo-status cc-demo-status--${row.tone}`}>
              {row.status}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export type BrowserSite = {
  url: string;
  brand: string;
  nav: string[];
  navCta: string;
  heading: string;
  text: string;
  cta: string;
  trust: string[];
  services: string[];
};

/** A made-up company's landing page inside a browser window. */
export function BrowserSitePanel({
  site,
  serviceIcons,
  visual,
}: {
  site: BrowserSite;
  serviceIcons: ReactNode[];
  visual: ReactNode;
}) {
  return (
    <Panel className="cc-demo__panel--main cc-demo__panel--flush cc-demo-browser">
      <div className="cc-demo-browser__chrome" aria-hidden="true">
        <span className="cc-demo-browser__dots">
          <i />
          <i />
          <i />
        </span>
        <span className="cc-demo-browser__url">
          <span className="cc-demo-browser__lock">{Icons.lock}</span>
          {site.url}
        </span>
      </div>
      <div className="cc-demo-site">
        <div className="cc-demo-site__nav">
          <span className="cc-demo-site__brand">
            <span className="cc-demo-serp__favicon" aria-hidden="true">
              {site.brand.charAt(0)}
            </span>
            {site.brand}
          </span>
          <span className="cc-demo-site__links">
            {site.nav.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </span>
          <span className="cc-demo-site__call">{site.navCta}</span>
        </div>
        <div className="cc-demo-site__hero">
          <div className="cc-demo-site__copy">
            <p className="cc-demo-site__heading">{site.heading}</p>
            <p className="cc-demo-site__text">{site.text}</p>
            <span className="cc-demo-site__cta">{site.cta}</span>
            <ul className="cc-demo-site__trust">
              {site.trust.map((item, index) => (
                <li key={item}>
                  {index === 0 ? (
                    <span className="cc-demo-places__star" aria-hidden="true">
                      {Icons.star}
                    </span>
                  ) : (
                    <span className="cc-demo-site__check" aria-hidden="true" />
                  )}
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="cc-demo-site__visual" aria-hidden="true">
            {visual}
          </div>
        </div>
        <ul className="cc-demo-site__services">
          {site.services.map((service, index) => (
            <li key={service}>
              <span className="cc-demo-site__service-icon" aria-hidden="true">
                {serviceIcons[index % serviceIcons.length]}
              </span>
              {service}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}

/** A share meter row; `logo` (an image) or `badge` (a letter) marks the item. */
export type Meter = {
  label: string;
  share: number;
  valueText: string;
  logo?: string;
  badge?: string;
};

/** Small logo (or letter badge) in front of a row label. */
export function Mark({ logo, badge }: { logo?: string; badge?: string }) {
  if (logo) {
    return (
      <Image className="cc-demo-mark" src={logo} alt="" width={16} height={16} unoptimized />
    );
  }
  if (badge) {
    return (
      <span className="cc-demo-mark cc-demo-mark--badge" aria-hidden="true">
        {badge}
      </span>
    );
  }
  return null;
}

/** Horizontal meters (share 0–1) with an optional logo, a label and a value. */
export function MeterList({ items }: { items: Meter[] }) {
  return (
    <ul className="cc-demo-list cc-demo-list--meter">
      {items.map((item) => (
        <li key={item.label}>
          <span
            className="cc-demo-list__meter"
            aria-hidden="true"
            style={{ "--cc-w": item.share } as CSSProperties}
          />
          <Mark logo={item.logo} badge={item.badge} />
          <span className="cc-demo-list__label">{item.label}</span>
          <span className="cc-demo-list__value">{item.valueText}</span>
        </li>
      ))}
    </ul>
  );
}
